/**
 * OralEcosystemMap — Phase D Section 11 — interactive biological pathway.
 *
 * Renders the `ecosystem_map` from the snapshot as a click-through SVG
 * pathway: Diet → Sugar → Microbiome → Inflammation → Enamel → Perio →
 * Long-term oral health. Each node opens an educational detail panel
 * describing what influences it, what worsens it, what improves it, and
 * the relevant interventions.
 */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const NODE_DETAILS = {
  diet: {
    bg: {
      what: "Дневният модел на хранене — съдържание на захари, фибри, протеини и омега-3.",
      worsens: ["Често хранене (>5/ден)", "Високо съдържание на добавени захари", "Ниски фибри"],
      improves: ["Средиземноморски модел", "Полифеноли (плодове, зеленчуци, зелен чай)", "Адекватен протеин (≥ 1 g/kg)"],
    },
    en: {
      what: "Daily eating pattern — sugar, fiber, protein and omega-3 content.",
      worsens: ["Frequent eating (>5/day)", "High added sugars", "Low fiber"],
      improves: ["Mediterranean pattern", "Polyphenols (berries, veg, green tea)", "Adequate protein (≥ 1 g/kg)"],
    },
    es: {
      what: "Patrón alimentario diario — contenido de azúcar, fibra, proteína y omega-3.",
      worsens: ["Comidas frecuentes (>5/día)", "Alto contenido de azúcares añadidos", "Bajo contenido de fibra"],
      improves: ["Patrón mediterráneo", "Polifenoles (bayas, verduras, té verde)", "Proteína adecuada (≥ 1 g/kg)"],
    },
  },
  sugar: {
    bg: {
      what: "Експозиция на ферментируеми въглехидрати (захар, бяло брашно).",
      worsens: ["Сладки напитки", "Чести закуски", "Сухи плодове на празен стомах"],
      improves: ["WHO лимит < 25g/ден", "Захар винаги с протеин/мазнина", "Ксилитол след хранене"],
    },
    en: {
      what: "Exposure to fermentable carbohydrates (sugar, refined flour).",
      worsens: ["Sweet drinks", "Frequent snacking", "Dried fruits on empty stomach"],
      improves: ["WHO < 25g/day", "Sugar always with protein/fat", "Xylitol after meals"],
    },
  },
  microbiome: {
    bg: {
      what: "Оралният микробиом — баланс между протективни и патогенни видове.",
      worsens: ["Дисбиоза → S. mutans, P. gingivalis", "Пушене", "Чести антибиотици"],
      improves: ["Фибри + полифеноли", "L. reuteri пробиотик", "Регулярни дентални прегледи"],
    },
    en: {
      what: "Oral microbiome — balance between protective and pathogenic species.",
      worsens: ["Dysbiosis → S. mutans, P. gingivalis", "Smoking", "Frequent antibiotics"],
      improves: ["Fiber + polyphenols", "L. reuteri probiotic", "Regular dental check-ups"],
    },
  },
  inflammation: {
    bg: {
      what: "Системно възпаление измерено чрез hs-CRP, IL-6, TNF-α.",
      worsens: ["Затлъстяване", "Инсулинова резистентност", "Хроничен стрес", "Лош сън"],
      improves: ["Омега-3 ≥ 8% индекс", "Витамин D ≥ 40 ng/ml", "Сън 7-9 ч", "Спорт 150 мин/седм."],
    },
    en: {
      what: "Systemic inflammation measured via hs-CRP, IL-6, TNF-α.",
      worsens: ["Obesity", "Insulin resistance", "Chronic stress", "Poor sleep"],
      improves: ["Omega-3 ≥ 8% index", "Vit D ≥ 40 ng/ml", "Sleep 7-9 h", "150 min/wk exercise"],
    },
  },
  enamel: {
    bg: {
      what: "Външният минерален слой на зъба — устойчивост на киселина и реминерализация.",
      worsens: ["Чести pH < 5.5 епизоди", "Гастро-езофагеален рефлукс", "Сухота в устата"],
      improves: ["Калций + фосфор + Vit D", "Флуоридна паста 2×/ден", "Ксилитол → буфериране"],
    },
    en: {
      what: "Tooth's outer mineral layer — acid resistance and remineralization.",
      worsens: ["Frequent pH < 5.5 episodes", "GERD reflux", "Dry mouth"],
      improves: ["Calcium + phosphorus + Vit D", "Fluoride toothpaste 2×/day", "Xylitol → buffering"],
    },
  },
  perio: {
    bg: {
      what: "Венци, пародонциум, кост — поддържат зъбите в челюстта.",
      worsens: ["Зъбна плака", "Пушене", "Диабет", "Инфламаторни цитокини"],
      improves: ["Дневен флос/интердентално миене", "Омега-3 (резолюция)", "Контрол на HbA1c < 7%"],
    },
    en: {
      what: "Gingiva, periodontium, bone — support teeth in the jaw.",
      worsens: ["Dental plaque", "Smoking", "Diabetes", "Inflammatory cytokines"],
      improves: ["Daily floss/interdental brushing", "Omega-3 (resolution)", "HbA1c < 7% control"],
    },
  },
  long_term: {
    bg: {
      what: "Дългосрочна устойчивост — задържане на собствените зъби и здрави венци.",
      worsens: ["Кумулативна загуба на емайл", "Хронично пародонтално възпаление"],
      improves: ["Поддръжка на горните 5 + редовни прегледи", "Контрол на метаболитни рискове"],
    },
    en: {
      what: "Long-term resilience — keeping your own teeth + healthy gums.",
      worsens: ["Cumulative enamel loss", "Chronic periodontal inflammation"],
      improves: ["Maintain the 5 above + regular check-ups", "Control metabolic risks"],
    },
  },
};

// Layout positions (SVG viewBox 1000x520). Larger boxes so labels fit comfortably.
const NODE_POS = {
  diet:         { x: 30,  y: 240, w: 170, h: 70 },
  sugar:        { x: 230, y: 240, w: 200, h: 70 },
  microbiome:   { x: 460, y: 240, w: 200, h: 70 },
  inflammation: { x: 690, y: 120, w: 180, h: 70 },
  enamel:       { x: 690, y: 360, w: 180, h: 70 },
  perio:        { x: 900, y: 120, w: 240, h: 70 },
  long_term:    { x: 900, y: 360, w: 240, h: 70 },
};

const NODE_COLORS = {
  diet:         "#F97316",
  sugar:        "#EF4444",
  microbiome:   "#A855F7",
  inflammation: "#EC4899",
  enamel:       "#0EA5E9",
  perio:        "#10B981",
  long_term:    "#6366F1",
};

export default function OralEcosystemMap({ ecosystemMap, isDark, lang = "bg", cardCls }) {
  const bg = lang === "bg";
  const [selected, setSelected] = useState(null);

  const t = bg
    ? {
        title: "Интерактивна екосистемна карта",
        subtitle: "Кликнете върху възел, за да видите какво го влияе, какво го влошава и как да го подобрите.",
        what: "Какво е това?",
        worsens: "Какво го влошава",
        improves: "Какво го подобрява",
        close: "Затвори",
      }
    : {
        title: "Interactive ecosystem map",
        subtitle: "Click a node to see what influences it, what worsens it, and how to improve it.",
        what: "What is this?",
        worsens: "What worsens it",
        improves: "What improves it",
        close: "Close",
      };

  const nodes = ecosystemMap?.nodes || [];
  const edges = ecosystemMap?.edges || [];

  const detail = selected ? NODE_DETAILS[selected]?.[bg ? "bg" : "en"] : null;
  const selNode = nodes.find((n) => n.id === selected);

  return (
    <div className={`${cardCls} p-5`} data-testid="dhi-ecosystem-map-card">
      <h3 className="text-lg font-bold mb-1">{t.title}</h3>
      <p className="text-xs opacity-60 mb-4">{t.subtitle}</p>

      <div className="overflow-x-auto">
        <svg
          viewBox="0 0 1180 530"
          className="w-full h-auto"
          style={{ minWidth: 760 }}
        >
          {/* Edges */}
          {edges.map(([from, to], i) => {
            const a = NODE_POS[from];
            const b = NODE_POS[to];
            if (!a || !b) return null;
            const x1 = a.x + a.w; const y1 = a.y + a.h / 2;
            const x2 = b.x;        const y2 = b.y + b.h / 2;
            return (
              <g key={i}>
                <line
                  x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke={isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)"}
                  strokeWidth="2"
                  strokeDasharray="3 3"
                />
                {/* Arrow head */}
                <polygon
                  points={`${x2},${y2} ${x2 - 8},${y2 - 4} ${x2 - 8},${y2 + 4}`}
                  fill={isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)"}
                />
              </g>
            );
          })}

          {/* Nodes */}
          {nodes.map((n) => {
            const p = NODE_POS[n.id];
            if (!p) return null;
            const label = bg ? n.label_bg : n.label_en;
            const isActive = selected === n.id;
            const color = NODE_COLORS[n.id] || "#94A3B8";
            return (
              <g
                key={n.id}
                onClick={() => setSelected(n.id)}
                style={{ cursor: "pointer" }}
                data-testid={`map-node-${n.id}`}
              >
                <rect
                  x={p.x} y={p.y} width={p.w} height={p.h}
                  rx="12"
                  fill={isActive ? color : (isDark ? "rgba(255,255,255,0.05)" : "white")}
                  stroke={color}
                  strokeWidth={isActive ? 4 : 2}
                />
                <text
                  x={p.x + p.w / 2}
                  y={p.y + p.h / 2 + 5}
                  textAnchor="middle"
                  fontSize="15"
                  fontWeight="700"
                  fill={isActive ? "white" : (isDark ? "white" : "#1A1A2E")}
                  style={{ pointerEvents: "none" }}
                >
                  {label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <AnimatePresence>
        {detail && (
          <motion.div
            key={selected}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mt-3"
          >
            <div className={`p-4 rounded-xl border ${isDark ? "bg-white/[0.02] border-white/10" : "bg-white border-gray-200"}`} data-testid={`map-detail-${selected}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-sm flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full inline-block"
                    style={{ background: NODE_COLORS[selected] }}
                  />
                  {selNode && (bg ? selNode.label_bg : selNode.label_en)}
                </div>
                <button
                  onClick={() => setSelected(null)}
                  className="text-[10px] opacity-60 hover:opacity-100"
                >
                  ✕ {t.close}
                </button>
              </div>
              <p className="text-xs opacity-80 mb-3">{detail.what}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] font-bold text-rose-600 dark:text-rose-400 mb-1">▼ {t.worsens}</div>
                  <ul className="space-y-0.5 text-xs">
                    {detail.worsens.map((w, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-rose-500 mt-0.5">·</span>
                        <span className="opacity-80">{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 mb-1">▲ {t.improves}</div>
                  <ul className="space-y-0.5 text-xs">
                    {detail.improves.map((w, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-emerald-500 mt-0.5">·</span>
                        <span className="opacity-80">{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
